import { addToast } from "@app/components/toast/toast.service";
import {
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import AuthorService from "@app/services/http/author.service";
import { selectAuth } from "@app/store/auth";
import { Author, BookOfAuthor, Meta } from "@app/types";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import BookOfAuthorItem from "./components/book-of-author";
import Pagination from "@app/components/pagination";

function AuthorDetailPage() {
  const { id } = useParams();
  const [author, setAuthor] = useState<Author>();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<Meta>();
  const [books, setBooks] = useState<BookOfAuthor[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );

  const navigate = useNavigate();

  const { id: userId } = useSelector(selectAuth);

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    if (!isNaN(Number(id))) {
      AuthorService.getDetail(Number(id), abortController.signal)
        .then((res) => {
          setAuthor(res.data);
          window.scrollTo({ top: 0, behavior: "smooth" });
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      navigate(RoutePath.HOME);
    }
  }, [id]);

  useEffect(() => {
    if (!isNaN(Number(id))) {
      AuthorService.getBooksOfAuthor(
        Number(id),
        requestParams,
        abortController.signal
      ).then((res) => {
        setBooks(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }

        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    } else {
      navigate(RoutePath.HOME);
    }
  }, [id, requestParams]);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      pageNum: page,
    };

    setRequestParams(newRequestParams);
  };

  const handleFollowClick = () => {
    AuthorService.followAuthor(Number(id)).then(() => {
      AuthorService.getDetail(Number(id)).then((res) => {
        setAuthor(res.data);

        if (res.data.isFollowing) {
          addToast({ text: SystemMessage.FOLLOW_AUTHOR_SUCCESSFULLY });
        } else {
          addToast({ text: SystemMessage.UNFOLLOW_AUTHOR_SUCCESSFULLY });
        }
      });
    });
  };

  return (
    <div className="px-[100px]">
      <div>
        <div className="flex justify-between items-center mt-10">
          <div className="flex items-center gap-6">
            <div>
              {!loading && author ? (
                <div className="size-[220px] rounded-full">
                  <img
                    className="size-[220px] rounded-full object-cover"
                    src={author.thumbnail}
                    alt=""
                  />
                </div>
              ) : (
                <Skeleton circle className="size-[220px]" />
              )}
            </div>
            <div className="flex flex-col gap-6">
              <div>
                {!loading ? (
                  <div className="text-[#171A20] font-[SVN-Gilroy] text-[28px] font-semibold">
                    {author?.fullName}
                  </div>
                ) : (
                  <Skeleton className="w-[200px] h-[34px]" />
                )}
              </div>
              <div className="flex gap-11">
                <div className="flex flex-col items-center">
                  <div className="leading-8 text-[#171A20] font-[SVN-Gilroy] text-2xl font-semibold">
                    {!loading ? (
                      author?.bookCount
                    ) : (
                      <Skeleton className="w-9" />
                    )}
                  </div>
                  <div className="text-[#424E62] font-[SVN-Gilroy] text-sm font-medium">
                    Đầu sách
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="leading-8 text-[#171A20] font-[SVN-Gilroy] text-2xl font-semibold">
                    {!loading ? (
                      author?.followerCount
                    ) : (
                      <Skeleton className="w-9" />
                    )}
                  </div>
                  <div className="text-[#424E62] font-[SVN-Gilroy] text-sm font-medium">
                    Người theo dõi
                  </div>
                </div>
                <div>
                  {!loading && !!userId ? (
                    !author?.isFollowing ? (
                      <button
                        className="text-sm pl-2 pr-4 rounded-[20px] leading-10 h-10 bg-gradient-to-b from-[#5A82D7] to-[#4667CA] text-white"
                        onClick={handleFollowClick}
                      >
                        <div className="flex gap-[6px]">
                          <img src={Images.PlusIcon.default} alt="" />
                          <div>Theo dõi</div>
                        </div>
                      </button>
                    ) : (
                      <div
                        className="size-16 rounded-full bg-[#ECEEF2] flex items-center justify-center cursor-pointer"
                        onClick={handleFollowClick}
                      >
                        <img
                          className="size-6"
                          src={Images.ProfileTickIcon.default}
                          alt=""
                        />
                      </div>
                    )
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-24">
          <div className="text-[#171A20] font-[SVN-Gilroy] text-[32px] font-bold">
            Tất Cả Sách
          </div>
          {!loading && books.length ? (
            <div>
              <div className="mt-9 flex flex-wrap gap-6">
                {books.map((book) => (
                  <div key={book.id}>
                    <BookOfAuthorItem book={book} />
                  </div>
                ))}
              </div>
              {meta && (
                <div className="flex justify-center mt-3">
                  <Pagination
                    currentPage={meta.pageNum}
                    totalPages={meta.totalPage}
                    onPageChange={onPageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-8 flex-wrap">
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
              <div>
                <Skeleton className="w-[200px] h-[300px] rounded-lg" />
                <Skeleton className="w-[200px] mt-1 h-7" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthorDetailPage;
